import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, BookOpen } from "lucide-react";
import { renderMarkdown, fetchManualText } from "../lib/markdown";
import dict from "../i18n/es-MX";

// Full-screen in-app viewer for the operator manual. Rendered as a plain
// screen (not a floating modal) so it plays nicely with the mobile shell —
// the tab bar stays hidden behind because this route is rendered outside
// the MobileShell layout.
export default function ManualOperador() {
    const navigate = useNavigate();
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let alive = true;
        setLoading(true); setErr("");
        fetchManualText("manual-operador.md")
            .then((t) => { if (alive) setContent(t); })
            .catch(() => { if (alive) setErr(dict.manuals.loadError); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const html = useMemo(() => renderMarkdown(content), [content]);

    const close = () => {
        // Prefer going back; if there is no history (deep link), fall to /campo.
        if (window.history.length > 1) navigate(-1);
        else navigate("/campo", { replace: true });
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col" data-testid="manual-operador-view">
            <div
                className="h-1"
                style={{ background: "repeating-linear-gradient(135deg, #F97316 0 18px, #0f172a 18px 36px)" }}
            />
            <header
                className="sticky top-0 z-10 h-14 bg-slate-900/90 backdrop-blur border-b border-slate-800 px-3 flex items-center justify-between"
                data-testid="manual-operador-header"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <div className="w-9 h-9 rounded-md bg-brand-orange/15 text-brand-orange flex items-center justify-center shrink-0">
                        <BookOpen size={18}/>
                    </div>
                    <h1 className="font-display text-base font-bold tracking-tight truncate">
                        {dict.manuals.items["manual-operador.md"].title}
                    </h1>
                </div>
                <button
                    type="button"
                    onClick={close}
                    data-testid="manual-operador-close"
                    aria-label={dict.manuals.close}
                    className="w-10 h-10 rounded-md hover:bg-slate-800 text-slate-400 hover:text-slate-100 inline-flex items-center justify-center"
                >
                    <X size={20}/>
                </button>
            </header>

            <main
                className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 max-w-3xl w-full mx-auto"
                data-testid="manual-operador-content"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
                {loading && (
                    <div className="py-12 text-center text-slate-500">
                        <Loader2 className="animate-spin inline mr-2"/>{dict.common.loading || "Cargando…"}
                    </div>
                )}
                {!loading && err && (
                    <div className="rounded-lg border border-red-500/40 bg-red-900/20 p-4 text-sm text-red-300" data-testid="manual-operador-error">
                        {err}
                    </div>
                )}
                {!loading && !err && (
                    <article className="prose prose-invert max-w-none" data-testid="manual-operador-article">
                        <div dangerouslySetInnerHTML={{ __html: html }}/>
                    </article>
                )}
            </main>
        </div>
    );
}
